import React, { useMemo, useState } from "react";
import {
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	ComposedChart,
} from "recharts";
import {
	BUCKET_COLORS,
	BUCKET_ORDER,
	USERS_EARNING_LABELS,
	bucketKey,
	bucketsToRow,
	formatUsersEarningForTable,
} from "./usersEarningUtils";

/**
 * Single cohort: stacked chart + table for day_wise_data[].usersEarning (D0…D15)
 */
export const UsersEarningDayPanel = ({ dayWiseData = [], totalUsers = 0 }) => {
	const hasData = dayWiseData.some((d) => (d?.usersEarning ?? []).length > 0);
	if (!hasData) return null;

	const chartData = dayWiseData.map((d) => ({
		name: d.day,
		...bucketsToRow(d.usersEarning),
	}));

	return (
		<div>
			<h3 style={{ margin: "0 0 4px 0" }}>Partner Earnings Buckets (cumulative by day)</h3>
			<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 16px 0" }}>
				Cumulative partner postback coins through each day (D0…Dn). Summary wallet cards above may differ — those use total user earnings (all sources).
			</p>
			<ResponsiveContainer width="100%" height={340}>
				<ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
					<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
					<XAxis dataKey="name" tick={{ fontSize: 12 }} />
					<YAxis tick={{ fontSize: 12 }} />
					<Tooltip
						formatter={(value, name) => [Number(value).toLocaleString(), name]}
					/>
					<Legend wrapperStyle={{ fontSize: 12 }} />
					{BUCKET_ORDER.map((id) => (
						<Bar
							key={String(id)}
							dataKey={bucketKey(id)}
							name={USERS_EARNING_LABELS[id]}
							stackId="earn"
							fill={BUCKET_COLORS[id]}
							maxBarSize={40}
						/>
					))}
				</ComposedChart>
			</ResponsiveContainer>

			<div style={{ overflowX: "auto", marginTop: 20 }}>
				<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
					<thead>
						<tr>
							<th style={th}>Day</th>
							{BUCKET_ORDER.map((id) => (
								<th key={String(id)} style={{ ...th, textAlign: "right" }}>
									{USERS_EARNING_LABELS[id]}
								</th>
							))}
							<th style={{ ...th, textAlign: "right" }}>Total</th>
						</tr>
					</thead>
					<tbody>
						{dayWiseData.map((row, i) => {
							const buckets = formatUsersEarningForTable(row.usersEarning);
							const sum = buckets.reduce((s, b) => s + b.total_users, 0);
							const mismatch = totalUsers > 0 && sum !== totalUsers;
							return (
								<tr key={row.day} style={{ background: i % 2 === 0 ? "#fff" : "#F9FAFB" }}>
									<td style={{ ...td, fontWeight: 600 }}>{row.day}</td>
									{buckets.map((b) => (
										<td key={String(b.id)} style={{ ...td, textAlign: "right" }}>
											{b.total_users.toLocaleString()}
										</td>
									))}
									<td style={{ ...td, textAlign: "right", fontWeight: 600, color: mismatch ? "#EF4444" : undefined }}>
										{sum.toLocaleString()}
										{mismatch ? " !" : ""}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

/**
 * Range mode: pick Dn, show each registration date's bucket mix (stacked)
 */
export const UsersEarningRangePanel = ({ cohorts = [] }) => {
	const dayOptions = useMemo(() => {
		let longest = [];
		cohorts.forEach((c) => {
			const dw = c?.day_wise_data ?? [];
			if (dw.length > longest.length) longest = dw;
		});
		return longest.map((d) => String(d.day));
	}, [cohorts]);

	const [dayFilter, setDayFilter] = useState(dayOptions[0] ?? "D0");
	const activeDay = dayOptions.includes(dayFilter) ? dayFilter : dayOptions[0] ?? "D0";

	const hasAny = cohorts.some((c) =>
		(c?.day_wise_data ?? []).some((d) => (d?.usersEarning ?? []).length > 0)
	);
	if (!hasAny) return null;

	const chartData = cohorts.map((c) => {
		const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(activeDay));
		return {
			name: c?.cohort_date ?? "",
			totalUsers: safeNum(c?.total_users),
			...bucketsToRow(entry?.usersEarning),
		};
	});

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
				<div>
					<h3 style={{ margin: 0 }}>Partner Earnings Buckets — {activeDay}</h3>
					<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0 0" }}>
						Cumulative partner postback coins through <strong>{activeDay}</strong>, per registration date. Stack = users in each range.
					</p>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Day:</span>
					<select
						value={activeDay}
						onChange={(e) => setDayFilter(e.target.value)}
						style={{
							padding: "8px 12px",
							border: "1px solid var(--border-color, #D1D5DB)",
							borderRadius: 8,
							fontSize: 13,
							fontWeight: 600,
							background: "#fff",
							cursor: "pointer",
						}}
					>
						{dayOptions.map((d) => (
							<option key={d} value={d}>{d}</option>
						))}
					</select>
				</div>
			</div>

			<ResponsiveContainer width="100%" height={340}>
				<ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
					<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
					<XAxis dataKey="name" tick={{ fontSize: 12 }} />
					<YAxis tick={{ fontSize: 12 }} />
					<Tooltip formatter={(value, name) => [Number(value).toLocaleString(), name]} />
					<Legend wrapperStyle={{ fontSize: 12 }} />
					{BUCKET_ORDER.map((id) => (
						<Bar
							key={String(id)}
							dataKey={bucketKey(id)}
							name={USERS_EARNING_LABELS[id]}
							stackId="earn"
							fill={BUCKET_COLORS[id]}
							maxBarSize={40}
						/>
					))}
				</ComposedChart>
			</ResponsiveContainer>

			<div style={{ overflowX: "auto", marginTop: 20 }}>
				<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
					<thead>
						<tr>
							<th style={th}>Registration Date</th>
							{BUCKET_ORDER.map((id) => (
								<th key={String(id)} style={{ ...th, textAlign: "right" }}>
									{USERS_EARNING_LABELS[id]}
								</th>
							))}
							<th style={{ ...th, textAlign: "right" }}>Total</th>
						</tr>
					</thead>
					<tbody>
						{cohorts.map((c, i) => {
							const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(activeDay));
							const buckets = formatUsersEarningForTable(entry?.usersEarning);
							const sum = buckets.reduce((s, b) => s + b.total_users, 0);
							const total = safeNum(c?.total_users);
							const mismatch = total > 0 && sum !== total;
							return (
								<tr key={c?.cohort_date ?? i} style={{ background: i % 2 === 0 ? "#fff" : "#F9FAFB" }}>
									<td style={{ ...td, fontWeight: 600 }}>{c?.cohort_date ?? "—"}</td>
									{buckets.map((b) => (
										<td key={String(b.id)} style={{ ...td, textAlign: "right" }}>
											{b.total_users.toLocaleString()}
										</td>
									))}
									<td style={{ ...td, textAlign: "right", fontWeight: 600, color: mismatch ? "#EF4444" : undefined }}>
										{sum.toLocaleString()}
										{mismatch ? " !" : ""}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

const safeNum = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

const th = {
	padding: "12px 14px",
	textAlign: "left",
	background: "#F3F4F6",
	fontWeight: 700,
	borderBottom: "2px solid #E5E7EB",
	whiteSpace: "nowrap",
};
const td = {
	padding: "11px 14px",
	borderBottom: "1px solid #F3F4F6",
};

export default UsersEarningDayPanel;
